// TicketBAI helper (Basque Country e-invoicing: Araba / Bizkaia / Gipuzkoa).
//
// Full TicketBAI compliance requires signing the XML with the issuer's regional
// certificate and chaining each invoice to the previous one's signature. That
// signing step happens server-side with the digital certificate. This module
// builds the TBAI identifier, the QR payload (incl. the spec's CRC-8), and the
// XML skeleton so the rest of the flow (display QR, download, submit) can work.

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
const n2 = (v) => Number(v || 0).toFixed(2);

// CRC-8 (poly 0x07) as required for the QR "cr" parameter, 3-digit zero-padded.
function crc8(str) {
  let crc = 0;
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i);
    for (let b = 0; b < 8; b++) {
      crc = (crc & 0x80) ? ((crc << 1) ^ 0x07) & 0xff : (crc << 1) & 0xff;
    }
  }
  return String(crc).padStart(3, '0');
}

// QR base URLs differ by province (defaulting to Gipuzkoa).
const QR_BASE = {
  gipuzkoa: 'https://tbai.egoitza.gipuzkoa.eus/qr/',
  bizkaia: 'https://batuz.eus/QRTBAI/',
  araba: 'https://ticketbai.araba.eus/tbai/qrcode/',
};

export function buildTicketBai({ invoice, profile, province = 'gipuzkoa', prevSignature = '' }) {
  const nif = (profile?.cif_nif || '').replace(/[^A-Za-z0-9]/g, '');
  const d = new Date(invoice.issue_date);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  // Software-generated short signature stand-in (real value comes from the XAdES signature).
  const sig = (prevSignature || invoice.id || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 13).toUpperCase().padEnd(13, '0');

  // TBAI-{NIF}-{ddmmyy}-{13 sig chars}-{CRC8 of the preceding}
  const idBase = `TBAI-${nif}-${dd}${mm}${yy}-${sig}`;
  const tbaiId = `${idBase}-${crc8(idBase)}`;

  const base = QR_BASE[province] || QR_BASE.gipuzkoa;
  const qrBase = `${base}?id=${tbaiId}&s=${encodeURIComponent(invoice.series || 'F')}&nf=${encodeURIComponent(invoice.invoice_number)}&i=${n2(invoice.grand_total)}`;
  const qrUrl = `${qrBase}&cr=${crc8(qrBase)}`;

  return { tbaiId, qrUrl };
}

export function buildTicketBaiXML({ invoice, lines, profile, client, tbai }) {
  const itemLines = (lines || []).map(l => `
        <IDDetalleFactura>
          <DescripcionDetalle>${esc(l.description)}</DescripcionDetalle>
          <Cantidad>${Number(l.quantity || 0).toFixed(2)}</Cantidad>
          <ImporteUnitario>${n2(l.unit_price)}</ImporteUnitario>
          <ImporteTotal>${n2(l.total ?? l.quantity * l.unit_price)}</ImporteTotal>
        </IDDetalleFactura>`).join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<T:TicketBai xmlns:T="urn:ticketbai:emision">
  <Cabecera><IDVersionTBai>1.2</IDVersionTBai></Cabecera>
  <Sujetos>
    <Emisor>
      <NIF>${esc((profile?.cif_nif || '').replace(/[^A-Za-z0-9]/g, ''))}</NIF>
      <ApellidosNombreRazonSocial>${esc(profile?.company_name || '')}</ApellidosNombreRazonSocial>
    </Emisor>
    <Destinatarios>
      <IDDestinatario>
        <NIF>${esc((client?.tax_id || '').replace(/[^A-Za-z0-9]/g, ''))}</NIF>
        <ApellidosNombreRazonSocial>${esc(client?.name || 'Consumidor final')}</ApellidosNombreRazonSocial>
      </IDDestinatario>
    </Destinatarios>
  </Sujetos>
  <Factura>
    <CabeceraFactura>
      <SerieFactura>${esc(invoice.series || 'F')}</SerieFactura>
      <NumFactura>${esc(invoice.invoice_number)}</NumFactura>
      <FechaExpedicionFactura>${esc((invoice.issue_date || '').split('T')[0])}</FechaExpedicionFactura>
    </CabeceraFactura>
    <DatosFactura>
      <DescripcionFactura>${esc(invoice.reference || 'Factura')}</DescripcionFactura>
      <DetallesFactura>${itemLines}
      </DetallesFactura>
      <ImporteTotalFactura>${n2(invoice.grand_total)}</ImporteTotalFactura>
    </DatosFactura>
  </Factura>
  <HuellaTBAI>
    <IdentificadorTBAI>${esc(tbai.tbaiId)}</IdentificadorTBAI>
    <CodigoQR>${esc(tbai.qrUrl)}</CodigoQR>
  </HuellaTBAI>
</T:TicketBai>`;
}
